<template>
  <v-container v-if="creditLine.id" fluid class="pa-6 product-detail">
    <v-row dense class="main-equalizer equalize-columns">
      <v-col cols="12" md="9" class="left-column">
        <!-- =============================
          PRODUCTO Y SALDO VENCIDO/AL CORRIENTE
        ============================== -->
        <v-card class="section-card header-info" elevation="3">
          <v-card-text>
            <div class="text-h4 font-weight-bold mb-6">
              {{ creditLine.product?.name }} - {{ creditLine.folio }}
            </div>
            <v-row dense class="align-center">
              <v-col cols="12" md="7">
                <div v-if="creditLine.id" class="d-flex flex-column">
                  <!-- CON SALDO VENCIDO -->
                  <template v-if="totalBalance?.dueAmount > 0">
                    <div class="text-subtitle-1 font-weight-bold">
                      SALDO VENCIDO
                    </div>

                    <div class="text-h4 font-weight-bold">
                      {{ formatCurrency(totalBalance.dueAmount) }} {{ creditLine.moneda }}
                    </div>

                    <div class="d-flex flex-wrap mt-3">
                      <div class="mini-detail">
                        <span class="mini-label">Capital</span>
                        <span class="mini-value">
                          {{ formatCurrency(totalBalance.expiredCapital) }}
                        </span>
                      </div>

                      <div class="mini-detail">
                        <span class="mini-label">Intereses</span>
                        <span class="mini-value">
                          {{ formatCurrency(totalBalance.interests) }}
                        </span>
                      </div>

                      <div class="mini-detail">
                        <span class="mini-label">Moratorios</span>
                        <span class="mini-value">
                          {{ formatCurrency(totalBalance.latePaymentInterests) }}
                        </span>
                      </div>
                    </div>
                  </template>

                  <!-- SIN SALDO VENCIDO -->
                  <template v-else>
                    <div class="d-flex align-center mt-2">
                      <v-icon
                        color="success"
                        class="mr-2"
                        medium
                      >
                        mdi-check-circle
                      </v-icon>

                      <div class="text-h5 font-weight-medium success--text">
                        Al corriente
                      </div>
                    </div>

                    <div class="grey--text text--darken-1 mt-1">
                      No se registran pagos vencidos.
                    </div>
                  </template>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- =============================
          DISPOSICIONES
        ============================== -->
        <v-card class="section-card header-info mt-3" elevation="3">
          <v-card-text>
            <div class="text-h6 font-weight-bold mb-4">
                Mis disposiciones
            </div>

            <v-data-table
              :headers="drawdownHeaders"
              :items="credits"
              item-key="id"
              dense
              class="elevation-0"
            >
              <template v-slot:[`item.fechaInicioCredito`]="{ item }">
                {{ formatDateShortText(item.fechaInicioCredito) }}
              </template>

              <template v-slot:[`item.fechaFinCredito`]="{ item }">
                {{ formatDateShortText(item.fechaFinCredito) }}
              </template>

              <template v-slot:[`item.montoInicial`]="{ item }">
                {{ formatCurrency(item.montoInicial) }}
              </template>

              <template v-slot:[`item.daysOverDue`]="{ item }">
                {{ item.currentBalance?.daysOverDue }}
              </template>

              <template v-slot:[`item.simulatePayment`]="{ item }">
                <v-icon
                  small
                  color="primary"
                  style="cursor: pointer;"
                  @click="openSimulatePaymentModal(item)"
                >
                  mdi-calculator-variant
                </v-icon>
              </template>
            </v-data-table>
          </v-card-text>
          <simulate-settlement v-if="showSimulatePaymentModal"
            v-model="showSimulatePaymentModal"
            :credit="selectedCredit"
          />
        </v-card>

        <v-row dense class="flex-grow-1 bottom-sections mt-2">
          <!-- =============================
            ÚLTIMOS PAGOS
          ============================== -->
          <v-col cols="12" md="6" class="d-flex section-wrapper">
            <v-card class="section-card last-payment pa-4" elevation="3">
              <v-card-text>
                <div class="text-h6 font-weight-bold mb-4">
                  Últimos pagos realizados
                </div>

                <div v-if="paymentsHistory.length">
                  <v-data-table
                  :headers="lastPaymentsHeaders"
                  :items="lastPayments"
                  item-key="id"
                  dense
                  class="elevation-0"
                >
                  <template v-slot:[`item.payDate`]="{ item }">
                    {{ formatDateShortText(item.payDate) }}
                  </template>

                  <template v-slot:[`item.amount`]="{ item }">
                    {{ formatCurrency(item.amount) }}
                  </template>

                  <template v-slot:[`item.invoice`]="{ item }">
                    <v-menu offset-y>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          small
                          v-bind="attrs"
                          v-on="on"
                          :disabled="!item.invoice?.facturamaId"
                        >
                          <v-icon small color="primary">
                            mdi-download
                          </v-icon>
                        </v-btn>
                      </template>

                      <v-list dense>
                        <v-list-item @click="$emit('download-invoice', item, 'pdf')">
                          <v-list-item-icon>
                            <v-icon small color="primary">mdi-file-pdf-box</v-icon>
                          </v-list-item-icon>
                          <v-list-item-title>PDF</v-list-item-title>
                        </v-list-item>

                        <v-list-item @click="$emit('download-invoice', item, 'xml')">
                          <v-list-item-icon>
                            <v-icon small color="primary">mdi-xml</v-icon>
                          </v-list-item-icon>
                          <v-list-item-title>XML</v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </template>
                </v-data-table>

                <v-divider class="my-4" />

                  <!-- Historial de pagos -->
                  <div class="text-center">
                    <v-btn
                      color="primary"
                      outlined
                      small
                      :disabled="false"
                      @click="openHistoryPayments"
                    >
                      <v-icon left small>mdi-history</v-icon>
                      <span>{{ $vuetify.breakpoint.mdOnly ? 'Ver historial' : 'Ver historial de pagos' }}</span>
                    </v-btn>
                  </div>

                  <payments-history
                    v-model="showPaymentsHistory"
                    :payments="paymentsHistory"
                    :currency="creditLine.moneda"
                    :is-revolving="true"
                    @download-invoice="onDownloadPaymentInvoice"
                  />
                </div>

                <div class="payment-row" v-else-if="creditLine.id">
                  <span>
                    No se presentan pagos registrados hasta el momento.
                  </span>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- =============================
            PRÓXIMOS VENCIMIENTOS
          ============================== -->
          <v-col cols="12" md="6" class="d-flex section-wrapper">
            <v-card class="section-card last-payment pa-4" elevation="3">
              <v-card-text>
                <div class="text-h6 font-weight-bold mb-4">
                  Próximos vencimientos
                </div>

                <div
                  v-for="p in nextPayments"
                  :key="`${p.folio}-${p.total}`"
                  class="mb-4"
                >
                  <!-- Barra -->
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <div
                        class="urgency-bar-wrapper"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <div
                          class="urgency-bar"
                          :class="urgencyClass(p.daysTillEnd)"
                          :style="{ width: urgencyWidth(p.daysTillEnd) }"
                        />
                      </div>
                    </template>

                    <!--tooltip con distribución del pago-->
                    <div class="tooltip-breakdown">
                      <div><strong>Capital:</strong> {{ formatCurrency(p.capital) }} {{ p.currency }}</div>
                      <div><strong>Intereses:</strong> {{ formatCurrency(p.interest) }} {{ p.currency }}</div>
                      <v-divider class="my-1" />
                      <div><strong>Total:</strong> {{ formatCurrency(p.total) }} {{ p.currency }}</div>
                    </div>
                  </v-tooltip>

                  <!-- Contenido -->
                  <div class="d-flex justify-space-between align-center mt-1">
                    <div>
                      <div class="font-weight-bold text-subtitle-1">
                        {{ formatCurrency(p.total) }} {{ p.currency }}
                      </div>
                      <div class="caption grey--text">
                        {{ p.folio }} · {{ `Pago de ${p.capital ? 'capital' : 'intereses'}:`}} {{ formatDateShortText(p.date) }}
                      </div>
                    </div>

                    <div class="caption font-weight-medium">
                      <strong>
                        {{ p.daysTillEnd > 0 ? 'Vence en' : 'Vence'}} {{ p.daysTillEnd === 0 ? 'hoy' : p.daysTillEnd + (p.daysTillEnd === 1 ? ' día' : ' días') }}
                      </strong>
                    </div>
                  </div>
                </div>

                <div class="payment-row" v-if="creditLine.id && !nextPayments?.length">
                  <span>
                    No se encontraron vencimientos.
                  </span>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-col>

      <v-col cols="12" md="3" class="right-column">
        <!-- =============================
          TIPO DE CAMBIO
        ============================== -->
        <v-card class="section-card capital-progress pa-4" elevation="3">
          <div class="d-flex align-center justify-space-between">
            <div class="text-subtitle-1 font-weight-medium grey--text text--darken-1">
              Tipo de cambio
            </div>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <div
                  class="d-flex align-center"
                  v-bind="attrs"
                  v-on="on"
                  style="cursor: help;"
                >
                  <span class="text-subtitle-1 font-weight-bold mr-1">
                    {{ indicators?.USD }}
                  </span>

                  <span class="text-caption grey--text text--darken-1">
                    MXN / USD
                  </span>

                  <v-icon small class="ml-1" color="#46659B">
                    mdi-information-outline
                  </v-icon>
                </div>
              </template>

              <div>
                <strong>Fuente:</strong> {{ indicators?.SOURCE }} <br>
                <strong>Fecha:</strong> {{ formatDateShortText(indicators?.EXCHANGEDATE) }}
              </div>
            </v-tooltip>
          </div>
        </v-card>

        <!-- =============================
          USO DE LÍNEA
        ============================== -->
        <v-card class="section-card capital-progress pa-4 mt-2" elevation="3">
          <div class="text-h6 font-weight-bold mb-4 text-center">
            Uso de línea
          </div>

          <apexchart
            width="100%"
            type="radialBar"
            :options="chartOptions"
            :series="[balanceUsed]"
          />

          <div class="text-center mt-4">
            <div class="text-subtitle-2">Crédito disponible</div>
            <div class="text-h5 font-weight-bold mt-1">
              {{ formatCurrency(creditLine.balance?.[creditLine.moneda]?.available) }} {{ creditLine.moneda }}
            </div>
          </div>

          <v-divider class="my-4" />

          <div class="text-center mb-4">
            <div class="text-subtitle-2">Monto para liquidar</div>
            <div class="text-h6 font-weight-bold mt-1">
              {{ formatCurrency(totalBalance.toLiquidate) }} {{ creditLine.moneda }}
            </div>
          </div>

          <v-divider class="my-4" />

          <div class="text-center">
            <div class="text-subtitle-2">Dispocisiones en crédito</div>
            <div class="text-h6 font-weight-bold mt-1">{{ this.credits?.length }}</div>
          </div>
        </v-card>

        <!-- =============================
          ESTADO DE CUENTA
        ============================== -->
        <v-card class="section-card account-statement pa-4 mt-3" elevation="3">
          <v-card-text>
            <div class="text-h6 font-weight-bold mb-4 text-center">Estado de cuenta</div>

            <v-row dense>
              <v-col cols="12">
                <v-radio-group
                  v-model="statementType"
                  row
                  hide-details
                >
                  <v-radio
                    label="Línea"
                    value="line"
                  />
                  <v-radio
                    label="Disposición"
                    value="credit"
                  />
                </v-radio-group>
              </v-col>
            </v-row>

            <v-row dense v-if="statementType === 'credit'">
              <v-col cols="12">
                <v-select
                  v-model="selectedDraw"
                  :items="drawOptions"
                  label="Folio"
                  dense
                  outlined
                  hide-details
                />
              </v-col>
            </v-row>

            <v-row dense>
              <v-col cols="12">
                <v-select
                  v-model="selectedPeriod"
                  :items="periodOptions"
                  label="Periodo"
                  dense
                  outlined
                  hide-details
                />
              </v-col>
            </v-row>

            <v-divider class="my-4" />

            <div class="text-center">
              <v-btn
                color="primary"
                outlined
                small
                :disabled="!selectedPeriod || (!selectedDraw && statementType === 'credit')"
                @click="onDownloadAccountStatement"
              >
                <v-icon left small>mdi-download</v-icon>
                <span>{{ $vuetify.breakpoint.mdOnly ? 'Descargar' : 'Descargar edo. de cuenta' }}</span>
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- =============================
         DETALLE DEL CRÉDITO
    ============================== -->
    <v-row dense class="mt-2">
      <v-col cols="12">
        <v-card class="section-card credit-details pa-4" elevation="3">
          <v-card-text>
            <div class="d-flex align-center justify-space-between mb-4">
              <div class="text-h6 font-weight-bold">Detalle de la línea de crédito</div>
            </div>

            <v-row dense>
              <v-col cols="12" sm="6" md="4" lg="3">
                <div class="credit-label">Monto</div>
                <div class="credit-value">
                  {{ formatCurrency(creditLine.montoInicial) }} {{ creditLine.moneda }}
                </div>
              </v-col>

              <v-col cols="12" sm="6" md="4" lg="3">
                <div class="credit-label">Inicio del contrato</div>
                <div class="credit-value">{{ formatDateShortText(creditLine.fechaInicioCredito) }}</div>
              </v-col>

              <v-col cols="12" sm="6" md="4" lg="3">
                <div class="credit-label">Fin del contrato</div>
                <div class="credit-value">{{ formatDateShortText(creditLine.fechaFinCredito) }}</div>
              </v-col>

              <v-col cols="12" sm="6" md="4" lg="3">
                <div class="credit-label">Días del año</div>
                <div class="credit-value">{{ creditLine.diasAnio }}</div>
              </v-col>

              <v-col v-for="rate in currencyRates" :key="rate.id" cols="12" sm="6" md="4" lg="3">
                <div class="credit-label">Tasa ordinaria {{ rate.currency }}</div>

                <!-- TASA VARIABLE -->
                <template v-if="rate.interesBase !== 'FIXED'">
                  <div class="credit-value d-flex align-center">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <span
                          v-bind="attrs"
                          v-on="on"
                          style="cursor: help;"
                        >
                          {{ rate.interesBase }} + {{ formatRate(rate.interesBaseAdicional) }} %
                        </span>
                      </template>

                      <div>
                        <strong>Tasa variable:</strong> {{ interestRateValue[rate.currency] }} <br>
                        <strong>Fuente:</strong> {{ indicators.SOURCE }} <br>
                        <strong>Fecha:</strong>
                        {{ formatDateShortText(indicators.EXCHANGEDATE) }}
                      </div>
                    </v-tooltip>

                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          x-small
                          outlined
                          class="ml-1"
                          color="primary"
                          v-bind="attrs"
                          v-on="on"
                          @click="openRateHistory()"
                          aria-label="Ver historial de tasas"
                        >
                          <v-icon small>mdi-chart-timeline-variant</v-icon>
                        </v-btn>
                      </template>

                      <span>Ver historial de tasas</span>
                    </v-tooltip>
                  </div>
                </template>

                <!-- TASA FIJA -->
                <template v-else>
                  <div class="credit-value">
                    FIJA {{ formatRate(rate.tasaAnual / 100) }}%
                  </div>
                </template>
              </v-col>

              <v-col v-for="rate in currencyRates" :key="rate.moratoriumId" cols="12" sm="6" md="4" lg="3">
                <div class="credit-label">Tasa moratoria {{ rate.currency }}</div>
                <div
                  class="credit-value"
                  :class="{ 'moratoria-activa': latePaymentRateIsActive[rate.currency] }"
                >
                  {{ rate.interesBase === 'FIXED' ? 'FIJA ' + formatRate(rate.moraAnual / 100 || 0) + '%' : `${creditLine.product?.confs?.moratoriumMultiplier}x la tasa ordinaria` }}
                  <span v-if="latePaymentRateIsActive[rate.currency]" class="moratoria-tag">Activa</span>

                  <!-- Botón para ver historial de tasas moratorias, solo si el crédito es tasa variable -->
                  <v-tooltip v-if="rate.interesBase !== 'FIXED'" bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        x-small
                        outlined
                        class="ml-1"
                        color="primary"
                        v-bind="attrs"
                        v-on="on"
                        @click="openRateHistory(true)"
                        aria-label="Ver historial de tasas"
                      >
                        <v-icon small>mdi-chart-timeline-variant</v-icon>
                      </v-btn>
                    </template>

                    <span>Ver historial de tasas</span>
                  </v-tooltip>
                </div>
              </v-col>

              <rate-history
                v-model="showRateHistory"
                :data="rateHistory"
                :interestAdditional="showLatePaymentRateHistory ? 0 : parseFloat(creditLine.interesBaseAdicional || 0)"
              />
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import moment from 'moment'
import CreditStatus from '../../common/CreditStatus'
import CreditsManagerService from '../../services/CreditsManagerService'
import productMixin from '../../mixins/productMixin'
import PaymentsHistory from '../../components/Modal/PaymentsHistory.vue'
import RateHistory from '../../components/Modal/RateHistory.vue'
import CurrencyRateTypes from '../../common/CurrencyRateTypes'
import SimulateSettlement from '../../components/Modal/SimulateSettlement.vue'

export default {
  name: 'RevolvingDetail',
  mixins: [productMixin],
  components: { PaymentsHistory, RateHistory, SimulateSettlement },
  data () {
    return {
      creditLine: {},
      creditLineId: null,
      selectedPeriod: null,
      paymentsHistory: [],
      showPaymentsHistory: false,
      showRateHistory: false,
      showLatePaymentRateHistory: false,
      rateHistory: [],
      credits: [],
      statementType: 'line',
      selectedDraw: null,
      currencyRates: [],
      capitalPayments: [],
      showSimulatePaymentModal: false,
      selectedCredit: null,
      nextPayments: [],
      drawdownHeaders: [
        { text: 'Folio', value: 'folio', align: 'center' },
        { text: 'Inicio', value: 'fechaInicioCredito', align: 'center' },
        { text: 'Vencimiento', value: 'fechaFinCredito', align: 'center' },
        { text: 'Total', value: 'montoInicial', align: 'right' },
        { text: 'Moneda', value: 'moneda', align: 'center' },
        { text: 'Días de atraso', value: 'daysOverDue', align: 'center' },
        { text: 'Simular pago', value: 'simulatePayment', align: 'center', sortable: false }
      ],
      lastPaymentsHeaders: [
        { text: 'Folio', value: 'folio', align: 'center' },
        { text: 'Fecha', value: 'payDate', align: 'center' },
        { text: 'Monto', value: 'amount', align: 'right' },
        { text: 'Factura', value: 'invoice', align: 'center', sortable: false }
      ],
      nextPaymentHeaders: [
        { text: 'Folio', value: 'folio', align: 'center' },
        { text: 'Fecha', value: 'date', align: 'center' },
        { text: 'Capital', value: 'capital', align: 'right' },
        { text: 'Interés', value: 'interest', align: 'right' }
      ]
    }
  },
  watch: {
    activePortafolio (portafolio) {
      if (!this.$route.query.folio) return

      const creditLine = portafolio?.find(c => c.folio === this.$route.query.folio)
      if (!creditLine) {
        this.$swal({
          icon: 'error',
          title: 'Folio inexistente',
          text: 'Este producto no se encuentra dentro de tu portafolio.',
          confirmButtonText: 'Cerrar',
          confirmButtonColor: '#d93025'
        })
        this.redirectToHome()
      }

      this.creditLineId = creditLine.id
      this.loadCreditLine()
    },
    '$route.query.folio': {
      immediate: true,
      handler (newFolio) {
        if (newFolio) {
          this.creditLineId = this.activePortafolio?.find(c => c.folio === newFolio)?.id
          this.loadCreditLine()
        }
      }
    }
  },
  async beforeMount () {
    const isLoggedIn = this.$store?.getters?.isLoggedIn

    if (!isLoggedIn) {
      window.location.replace('/login')
    }
  },
  mounted () {
    if (!this.$route.query.folio) this.redirectToHome()
  },
  methods: {
    async loadCreditLine () {
      if (!this.creditLineId) return
      this.resetData()
      const loader = this.$loading.show()
      let nextPayments = []
      try {
        const response = await CreditsManagerService.getCreditLine(this.creditLineId)
        this.creditLine = response
        this.credits = response.disposiciones?.filter(c => [CreditStatus.ETAPA_1, CreditStatus.ETAPA_2, CreditStatus.ETAPA_3].includes(c.status)) || []
        this.credits = this.credits.sort((a, b) => new Date(a.fechaInicioCredito) - new Date(b.fechaInicioCredito))

        this.setCurrencyRates(this.creditLine?.currencyRates)

        for (const drawdown of this.credits) {
          drawdown.folioNumber = drawdown.folio.split('-')[1]
          const drawdownDetail = await CreditsManagerService.getCredit(drawdown.id)

          // Buscar próximos pagos
          const payments = drawdownDetail.tablaAmortizacion
            .filter(period =>
              moment(new Date(period.fechaFin)).startOf('day').isAfter(moment().startOf('day')) &&
                (!period.montoPagado || period.montoPago - period.montoPagado > 1) &&
                !drawdownDetail.tablaAmortizacion?.find(otherPeriod => otherPeriod.capitalizado === period.montoPago)
            )
            .map(period => {
              return {
                folio: drawdown.folio,
                date: moment(new Date(period.fechaFin)).format('YYYY-MM-DD'),
                currency: drawdown.moneda,
                total: period.montoPago - (period.montoPagado || 0),
                capital: period.pagoCapital,
                interest: period.pagoInteres,
                iva: period.iva,
                period: period.periodo,
                daysTillEnd: moment.tz(period.fechaFin, 'YYYY-MM-DD', 'America/Mexico_City').diff(moment().tz('America/Mexico_City'), 'days') + 1
              }
            })

          const interestPayments = payments.filter(payment => !payment.capital)
          const capitalPayments = payments.filter(payment => payment.capital && payment.daysTillEnd <= 90)

          if (interestPayments.length) nextPayments.push(interestPayments[0])
          if (capitalPayments.length) nextPayments = nextPayments.concat(capitalPayments)

          drawdown.nextPayment = payments[0] || {}
          drawdown.creditMovements = drawdownDetail.creditMovements
        }

        this.nextPayments = nextPayments.sort((a, b) => new Date(a.date) - new Date(b.date))

        // Historial de pagos
        const pastPayments = this.credits
          .map(c =>
            c.creditMovements.filter(m => !m.payment?.tratamiento).map(m => ({
              ...m,
              folio: c.folio,
              currency: c.moneda
            }))
          )
          .flat()

        this.paymentsHistory = pastPayments.map(p => {
          const currencyRate = this.currencyRates.find(r => r.currency === p.currency)
          const interestFixed = currencyRate.interesBase === 'FIXED'
          let paymentDateRate

          if (interestFixed) paymentDateRate = (currencyRate?.tasaAnual / 100)
          else {
            const appliedPaymentRate = this.credits.find(c => c.folio === p.folio)?.tasasAplicables?.find(t =>
              t.fecha === p.appliedPayDate || // Que tengan la misma fecha
            t.fecha.substring(0, 8) === p.appliedPayDate.substring(0, 8)) // O que pertenezcan al mismo mes

            paymentDateRate = parseFloat(appliedPaymentRate?.tasa || 0) + parseFloat(currencyRate?.interesBaseAdicional || 0)
          }

          // Pago anticipado: se resta el monto ideal a lo que pagó el cliente para sacar el monto que se va a últimos pagos
          const advancePayment = -(p.payment?.pago ?? 0) - p.amount

          return {
            ...p,
            interestFixed,
            paymentDateRate,
            advancePayment
          }
        })
      } catch (err) {
        console.error(err)
        this.$swal({
          icon: 'error',
          title: 'Ocurrió un error',
          text: 'Hubo un problema al cargar los detalles de tu crédito. Por favor inténtalo nuevamente.',
          confirmButtonText: 'Cerrar',
          confirmButtonColor: '#d93025'
        })
        this.redirectToHome()
      } finally {
        loader.hide()
      }

      if (this.paymentsHistory?.length) {
        this.paymentsHistory = await this.mapInvoicesToPayments(this.paymentsHistory, this.creditLine?.fechaInicioCredito)
      }
    },
    async onDownloadAccountStatement () {
      let selected = {}
      if (this.statementType === 'line') {
        selected = this.creditLine
      } else {
        selected = this.credits.find(c => c.id === this.selectedDraw)
      }

      this.downloadAccountStatement({
        id: selected.id,
        folio: selected.folio,
        statementType: this.statementType,
        selectedPeriod: this.selectedPeriod
      })
    },
    onDownloadPaymentInvoice (item, format) {
      this.downloadInvoice({
        id: item.invoice?.facturamaId,
        fileType: format,
        fileName: `Factura_CP_${this.creditLine.folio}_${item.payDate}`
      })
    },
    setCurrencyRates (recordCurrencyRates) {
      const currencyRates = []
      recordCurrencyRates.filter(_ => _.type === CurrencyRateTypes.ORDINARIA).forEach(_ => currencyRates.push({ ..._, tasaAnual: _.reservedFolioRate, ordinaryId: _.id }))
      recordCurrencyRates.filter(_ => _.type === CurrencyRateTypes.MORATORIA).forEach(_ => {
        const currencyRate = currencyRates.find(rate => rate.currency === _.currency)
        if (currencyRate) {
          currencyRate.moratoriumId = _.id
          currencyRate.moraAnual = _.reservedFolioRate
        } else currencyRates.push({ ..._, tasaAnual: 0, moraAnual: _.reservedFolioRate })
      })

      this.currencyRates = currencyRates.map(_ => _)
    },
    resetData () {
      this.creditLine = {}
      this.credits = []
      this.paymentsHistory = []
      this.currencyRates = []
      this.selectedPeriod = null
    },
    openHistoryPayments () {
      this.showPaymentsHistory = true
    },
    openRateHistory (isLatePaymentRate = false) {
      this.rateHistory = [
        ...new Map(
          this.credits
            .flatMap(draw =>
              isLatePaymentRate
                ? draw.tasasAplicablesMoratorio || []
                : draw.tasasAplicables || []
            )
            .map(item => [
              `${this.formatRate(item.tasa)}-${item.fecha}`,
              item
            ])
        ).values()
      ].sort(
        (a, b) => new Date(a.fecha) - new Date(b.fecha)
      )

      this.showLatePaymentRateHistory = isLatePaymentRate
      this.showRateHistory = true
    },
    openSimulatePaymentModal (drawdown) {
      this.selectedCredit = drawdown
      this.showSimulatePaymentModal = true
    },
    urgencyClass (days) {
      if (days <= 5) return 'urgency-high'
      if (days <= 15) return 'urgency-medium'
      return 'urgency-low'
    },

    urgencyWidth (days) {
      const maxDays = 30
      const percentage = Math.max(15, 100 - (days / maxDays) * 100)
      return `${percentage}%`
    }

  },
  computed: {
    lastPayments () {
      return Object.values(
        this.paymentsHistory.reduce((acc, payment) => {
          const folio = payment.folio

          if (
            !acc[folio] ||
      new Date(payment.payDate) > new Date(acc[folio].payDate)
          ) {
            acc[folio] = payment
          }

          return acc
        }, {})
      )
    },
    balanceUsed () {
      if (!this.creditLine.moneda) return 0
      const percentage = Math.round((this.creditLine?.balance[this.creditLine.moneda]?.used / this.creditLine.montoInicial) * 100)
      return percentage
    },
    totalBalance () {
      const dueAmount = this.credits?.map(c => c.currentBalance?.balance?.totalExigible || 0) || []
      const expiredCapital = this.credits?.map(c => c.currentBalance?.balance?.capitalVencido || 0) || []
      const interests = this.credits?.map(c => c.currentBalance?.balance?.interest || 0) || []
      const latePaymentInterests = this.credits?.map(c => c.currentBalance?.balance?.moratorium || 0) || []
      const toLiquidate = this.credits?.map(c => c.currentBalance?.balance?.toLiquidate || 0) || []

      return {
        toLiquidate: toLiquidate.reduce((sum, v) => sum + v, 0),
        dueAmount: dueAmount.reduce((sum, v) => sum + v, 0),
        expiredCapital: expiredCapital.reduce((sum, v) => sum + v, 0),
        interests: interests.reduce((sum, v) => sum + v, 0),
        latePaymentInterests: latePaymentInterests.reduce((sum, v) => sum + v, 0)
      }
    },
    interestRateValue () {
      const rates = {}

      this.currencyRates.forEach(r => {
        const baseValue = this.creditLine.interesBase?.toUpperCase().includes('SOFRAI') ? this.indicators.SOFRAI[this.creditLine.interesBase?.toUpperCase()] : this.indicators[this.creditLine.interesBase?.toUpperCase()]
        const percent = parseFloat(baseValue) + (parseFloat(this.creditLine.interesBaseAdicional) * 100)

        rates[r.currency] = `${percent.toFixed(2)}%`
      })
      return rates
    },
    periodOptions () {
      return this.getPeriodOptions(this.creditLine)
    },
    drawOptions () {
      return this.credits?.map(c => {
        return {
          text: c.folio,
          value: c.id
        }
      })
    },
    chartOptions () {
      return {
        chart: {
          sparkline: { enabled: true }
        },
        plotOptions: {
          radialBar: {
            hollow: {
              size: '60%'
            },
            track: {
              background: '#BBDEF0',
              strokeWidth: '70%'
            },
            dataLabels: {
              show: true,
              value: {
                fontSize: '36px',
                fontWeight: 'bold',
                offsetY: 5
              }
            }
          }
        },
        tooltip: {
          enabled: true,
          custom: () => {
            return `
            <div class="apex-tooltip">
              <div><strong>Saldo utilizado:</strong> ${this.formatCurrency(this.creditLine?.balance?.[this.creditLine.moneda]?.used)} ${this.creditLine.moneda}</div>
            </div>
          `
          }
        },
        fill: {
          colors: ['#142660']
        },
        labels: ['']
      }
    },
    latePaymentRateIsActive () {
      return this.currencyRates.reduce((acc, { currency }) => {
        acc[currency] = this.credits.some(c =>
          c.moneda === currency && (c.currentBalance?.daysOverDue || 0) > 0
        )
        return acc
      }, {})
    }
  }
}
</script>

<style scoped>

.product-detail {
  min-height: 100vh;
}

.equalize-columns {
  align-items: stretch !important;
}

.main-equalizer {
  display: flex;
  align-items: stretch !important;
}

.left-column,
.right-column {
  display: flex;
  flex-direction: column;
}

.bottom-sections {
  flex-grow: 1;
  display: flex;
  align-items: stretch !important;
}

.section-wrapper {
  display: flex;
  flex-direction: column;
}

.section-wrapper .section-card {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.text-h4,
.text-h5,
.text-h6,
.product-title,
.credit-label,
.table-label,
.payment-row span:first-child,
.section-card .text-h6,
.text-subtitle-2 {
  color: #1E293B !important;
}

.mini-detail {
  margin-right: 24px;
  margin-bottom: 6px;
  display: flex;
  flex-direction: column;
}

.mini-label {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.mini-value {
  font-size: 16px;
  font-weight: 700;
  color: #1a237e;
}

.payment-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
}

.payment-value,
.table-value,
.credit-value {
  font-weight: 700;
  color: #1a237e;
}

.credit-label {
  font-size: 13px;
  font-weight: 600;
}

.credit-value {
  font-size: 17px;
  font-weight: 700;
}

.moratoria-activa {
  color: #c62828;
  font-weight: bold;
  position: relative;
}

.moratoria-tag {
  background-color: #c62828;
  color: white;
  font-size: 0.7rem;
  padding: 2px 6px;
  border-radius: 4px;
  margin-left: 6px;
  text-transform: uppercase;
}

.urgency-bar-wrapper {
  width: 100%;
  height: 6px;
  background: #eee;
  border-radius: 4px;
  overflow: hidden;
}

.urgency-bar {
  height: 100%;
  border-radius: 4px;
  transition: width 0.3s ease;
}

.urgency-high {
  background: #E53935;
}

.urgency-medium {
  background: #FB8C00;
}

.urgency-low {
  background: #43A047;
}

.tooltip-breakdown {
  min-width: 180px;
  font-size: 12px;
  line-height: 1.4;
}

::v-deep .tooltip-breakdown {
  min-width: 180px;
}

</style>
